import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/forms/input.component';
import { useForm } from 'react-hook-form';
import Button from '../../../components/forms/button.component';
import { AlertService } from '../../../services/alert.service';
import useModal from '../../../hooks/use-close-modal.hook';
import { STORAGE } from '../../../constants';
import { LocalStorageService } from '../../../services/local-storage.service';
import LogData from '../../../components/logs/log-data.component';
import { AccountsService } from '../../../services/accounts.service';
import Icon from '../../../components/forms/icon.component';

interface ActivateAccountProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function ActivateAccount({ show, onRequestClose, payload }: ActivateAccountProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const user = LocalStorageService.get(STORAGE.SESSION);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<{ username: string }>({
    mode: 'onChange',
    defaultValues: { username: '' }
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const resetForm = () => {
    setIsProcessing(false);
    reset({ username: '' });
  };

  const onSubmit = async (data: { username: string }, buttonId: string) => {
    if (!data.username.trim()) {
      AlertService.toastError(t('account-page.modal-activate-account.error-username-required'));
      return;
    }

    if (data.username !== user.username) {
      AlertService.toastError(t('account-page.modal-activate-account.error-invalid-username'));
      return;
    }

    setIsProcessing(true);

    try {
      await AccountsService.activateAccount({
        account: payload?._id,
        username: data.username
      });
      onRequestClose(true);
      AlertService.toastSuccess(t('account-page.modal-activate-account.success-message'));
      resetForm();
      LogData({ accountId: payload?._id, username: data.username }, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div aria-hidden="true" className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-mw-900px modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body p-0">
            <div className="alert alert-dismissible d-flex flex-center flex-column py-10 px-10 px-lg-20 mb-0">
              <button
                type="button"
                className="position-absolute top-0 end-0 m-2 btn btn-icon btn-icon-success fs-2x"
                onClick={() => {
                  onRequestClose(false);
                  resetForm();
                }}
              >
                <Icon icon="ki-cross" size="fs-1" type="danger" />
              </button>
              <Icon icon="ki-shield-tick" size="fs-5tx" type="success" className="mb-5 fw-bolder" />
              <div className="text-center">
                <h1 className="fw-bolder mb-5">{t('account-page.modal-activate-account.title')}</h1>
                <div className="separator border-2 my-5"></div>
                <span className="badge badge-light-primary fs-6 mb-6">
                  <Icon icon="ki-briefcase" size="fs-3" type="primary" className="me-2" />
                  {payload?.name}
                </span>
                <div className="mb-10">
                  {t('account-page.modal-activate-account.warning-message')}
                  <br />
                  <br />
                  <strong>{t('account-page.modal-activate-account.confirmation')}</strong>
                </div>
                <form
                  onSubmit={handleSubmit((data) => onSubmit(data, 'activateAccount-8937403948'))}
                >
                  <div className="mb-10">
                    <label className="mb-0 d-flex justify-content-start required fw-bolder">
                      {t('account-page.modal-activate-account.label-username')}
                    </label>
                    <Input
                      placeholder={t('account-page.modal-activate-account.placeholder-username')}
                      requiredIndicator="required"
                      errors={errors.username}
                      {...register('username', {
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>
                  <div className="d-flex flex-center flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline btn-outline-success btn-active-success m-2"
                      onClick={() => onRequestClose(false)}
                    >
                      {t('account-page.modal-activate-account.btn-cancel')}
                    </button>
                    <Button
                      type="submit"
                      className="btn btn-success m-2"
                      isLoading={isSubmitting || isProcessing}
                    >
                      {t('account-page.modal-activate-account.btn-confirm')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActivateAccount;
